import { useMemo, useState } from 'react'
import qrcode from 'qrcode-generator'
import { LINK_KIND_LABEL, LinkKind, QR_KINDS, type EnvLink } from '@cs/shared'
import { api } from '../api.js'
import { describeError } from '../App.js'

/**
 * The QR code is generated here, in the page.
 *
 * The obvious alternative — an image URL from a QR service — would send the build
 * address to a third party every time anyone looked at the board, which is a strange
 * thing to do with a URL the two companies deliberately keep between themselves.
 * `qrcode-generator` has no dependencies of its own and runs entirely offline, so
 * the address never leaves the browser.
 *
 * Rendered as an inline SVG rather than a canvas so it stays sharp when someone
 * zooms in to scan it from across a desk, and so it prints.
 */
function QrSvg({ text, size = 176 }: { text: string; size?: number }) {
  const svg = useMemo(() => {
    // Type 0 = pick the smallest version that fits; 'M' tolerates a phone camera at
    // an angle, which is how these are actually scanned.
    const qr = qrcode(0, 'M')
    qr.addData(text)
    qr.make()
    const count = qr.getModuleCount()
    const cells: string[] = []
    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (qr.isDark(row, col)) cells.push(`M${col},${row}h1v1h-1z`)
      }
    }
    // A quiet zone of 4 modules is part of the spec; without it scanners struggle.
    const q = 4
    return {
      viewBox: `${-q} ${-q} ${count + q * 2} ${count + q * 2}`,
      path: cells.join(''),
    }
  }, [text])

  return (
    <svg
      width={size}
      height={size}
      viewBox={svg.viewBox}
      shapeRendering="crispEdges"
      role="img"
      aria-label="QRコード"
      style={{ background: '#fff', borderRadius: 6 }}
    >
      <path d={svg.path} fill="#000" />
    </svg>
  )
}

const BLANK = { kind: 'dev' as LinkKind, label: '', url: '', note: '' }

export function Links({
  room,
  links,
  canEdit,
  onChanged,
}: {
  room: string
  links: EnvLink[]
  canEdit: boolean
  onChanged: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [showQr, setShowQr] = useState<string | null>(null)
  const [form, setForm] = useState(BLANK)
  const [adding, setAdding] = useState(false)

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.upsertLink(room, {
        ...(editing ? { id: editing } : {}),
        kind: form.kind,
        label: form.label.trim(),
        url: form.url.trim(),
        note: form.note.trim() || null,
      })
      setEditing(null)
      setAdding(false)
      setForm(BLANK)
      onChanged()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (link: EnvLink) => {
    if (!window.confirm(`${link.label} を削除しますか？`)) return
    try {
      await api.deleteLink(room, link.id)
      onChanged()
    } catch (err) {
      setError(describeError(err))
    }
  }

  const beginEdit = (link: EnvLink) => {
    setEditing(link.id)
    setAdding(false)
    setForm({ kind: link.kind, label: link.label, url: link.url, note: link.note ?? '' })
  }

  const editor = (
    <div style={{ marginTop: 10 }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <select
          value={form.kind}
          onChange={(e) => setForm({ ...form, kind: e.target.value as LinkKind })}
          style={{ width: 'auto' }}
        >
          {(Object.keys(LINK_KIND_LABEL) as LinkKind[]).map((k) => (
            <option key={k} value={k}>
              {LINK_KIND_LABEL[k]}
            </option>
          ))}
        </select>
        <input
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="表示名 (例: 管理画面 STG)"
          style={{ flex: '1 1 180px' }}
        />
      </div>
      <div style={{ height: 8 }} />
      <input
        value={form.url}
        onChange={(e) => setForm({ ...form, url: e.target.value })}
        placeholder="https://..."
        autoComplete="off"
      />
      <div style={{ height: 8 }} />
      <input
        value={form.note}
        onChange={(e) => setForm({ ...form, note: e.target.value })}
        placeholder="メモ (任意・例: ログインは検証用アカウント)"
      />
      <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            setEditing(null)
            setAdding(false)
            setForm(BLANK)
          }}
        >
          やめる
        </button>
        <button
          className="primary"
          disabled={busy || !form.label.trim() || !form.url.trim()}
          onClick={save}
        >
          {editing ? '更新する' : '追加する'}
        </button>
      </div>
      <p className="muted" style={{ marginBottom: 0, fontSize: 12 }}>
        ここに入れた URL は相手の会社から見えます。URL に ID
        やパスワードを埋め込まないでください（検出した場合は保存できません）。
      </p>
    </div>
  )

  return (
    <div className="card">
      <h2>環境</h2>
      {error && <div className="error">{error}</div>}

      {links.length === 0 && !adding && (
        <p className="muted">
          まだ登録されていません。DEV / STG / PROD や配布用のビルドを登録しておくと、
          毎回チャットで聞かれずに済みます。
        </p>
      )}

      {links.map((l) => (
        <div key={l.id} style={{ marginBottom: 10 }}>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className={`pill ${l.kind}`}>{LINK_KIND_LABEL[l.kind]}</span>
            <strong>{l.label}</strong>
            {QR_KINDS.includes(l.kind) && (
              <button onClick={() => setShowQr(showQr === l.id ? null : l.id)}>
                {showQr === l.id ? 'QRを隠す' : 'QRを表示'}
              </button>
            )}
            {canEdit && (
              <>
                <button style={{ marginLeft: 'auto' }} onClick={() => beginEdit(l)}>
                  編集
                </button>
                <button className="danger" onClick={() => remove(l)}>
                  削除
                </button>
              </>
            )}
          </div>
          <div>
            <a href={l.url} target="_blank" rel="noreferrer noopener" style={{ wordBreak: 'break-all' }}>
              {l.url}
            </a>
          </div>
          {l.note && <div className="muted">{l.note}</div>}
          <div className="muted" style={{ fontSize: 12 }}>
            {l.updated_by_name} が {new Date(l.updated_at).toLocaleString('ja-JP')} に更新
          </div>

          {showQr === l.id && (
            <div style={{ marginTop: 8 }}>
              <QrSvg text={l.url} />
              <div className="muted" style={{ fontSize: 12, maxWidth: 260 }}>
                端末のカメラで読み取るとこの URL が開きます。
                <strong>この QR を読める人は誰でもダウンロードできます。</strong>
                画面を共有する場やスクリーンショットの扱いにご注意ください。
              </div>
            </div>
          )}

          {editing === l.id && editor}
        </div>
      ))}

      {canEdit && !adding && !editing && (
        <button
          onClick={() => {
            setAdding(true)
            setForm(BLANK)
          }}
        >
          環境を追加
        </button>
      )}
      {adding && editor}
    </div>
  )
}
